import classNames from 'classnames'
import { FC, ReactNode, useEffect, useState } from 'react'
import { ItemListElements } from 'next-seo/lib/types'

import SeoContainer, {
  SeoContainerProps,
} from 'components/util/containers/SeoContainer'
import { useRouter } from 'next/router'
import { Stars } from 'components/icons'
import Strings from 'project/localisation'
import { getPlans } from 'common/planData'
import { getAddons } from 'common/planData'
import Button from 'components/forms/Button'
import useShoppingBag from 'common/useShoppingBag'
import { AddonSlug, PlanSlug } from 'common/planSlugs'
import { TAddon, TPlanSummary } from 'common/cms-types'
import { generateSrcSet } from 'project/image-proxy-web'
import useWindowSize from 'components/util/useWindowSize'
import { I18NConfig } from 'next/dist/server/config-shared'
import { sectionGap, sectionGapSmall } from 'project/padding'
import { NextPageWithLayout } from '../types/nextPageWithLayout'
import PartnerHeroBanner from 'components/hero/PartnerHeroBanner'
import RightRobotCard from 'components/cards/new/common/RightRobotCard'
import { homeBreadcrumb } from 'components/util/containers/Breadcrumbs'
import { CrunchLogoPink } from 'components/icons'

const whoWeHelp = [
  'Architects',
  'Contractors',
  'Creative freelancers',
  'Dentists',
  'Freelancers',
  'IT consultants',
  'Interior designers',
  'Online retailers',
  'Plumbers',
  'Small businesses',
  'Startups',
  'Switching accountants',
  'Whatever your passion...',
]

const CrunchTestimonialCard: FC<{
  name: string
  testimonial: string
  className?: string
}> = ({ className, name, testimonial }) => {
  const size = useWindowSize()
  return (
    <div
      className={classNames(
        'card d-flex flex-column flex-1 h-auto justify-content-between align-items-center pt-7 pb-5 pt-lg-12 pb-lg-9 px-10',
        className,
      )}
      style={{
        minHeight: size.isLgOrHigher ? 400 : undefined,
      }}
    >
      <Stars />

      <span
        className='fs-fluid-18-24 text-center text-muted fw-semibold fst-italic mt-4'
        style={{
          maxWidth: 453,
        }}
      >
        {testimonial}
      </span>

      <h2 className='fs-fluid-24-34 lh-140 mt-4 text-center'>{name}</h2>
    </div>
  )
}

type Feature = {
  title: string
  features: {
    title: string
    description: string | ReactNode
  }[]
  imagePosition: 'left' | 'right'
  image: {
    src: string
    height: number
    width: number
    alt: string
  }
  children?: ReactNode
}

const FeatureSection: FC<Feature> = ({
  children,
  features,
  image,
  imagePosition,
  title,
}) => {
  const size = useWindowSize()
  if (!size.isLgOrHigher) {
    imagePosition = 'left'
  }
  return (
    <div className='d-flex flex-column flex-lg-row gap-5 gap-lg-17'>
      {imagePosition === 'left' && (
        <div className='d-flex col-12 col-lg-6 align-items-center'>
          <img
            {...generateSrcSet(image.src, image.width, image.height)}
            height={image.height}
            width={image.width}
            alt={image.alt}
            className='img-fluid'
          />
        </div>
      )}
      <div className='d-flex flex-column  lh-140 justify-content-center gap-4 gap-lg-7 col-12 col-lg-6'>
        <h2 className='fs-fluid-32-48 lh-105 fw-semibold color-pink4 mb-0'>
          {title}
        </h2>
        {children
          ? children
          : features.map((feature) => (
              <div key={feature.title}>
                <span className='fs-fluid-20-24 fw-semibold'>
                  {feature.title}
                </span>
                <span className='fw-semibold text-muted d-block mt-2'>
                  {feature.description}
                </span>
              </div>
            ))}
      </div>
      {imagePosition === 'right' && (
        <div className='d-flex col-12 col-lg-6 align-items-center'>
          <img
            {...generateSrcSet(image.src, image.width, image.height)}
            height={image.height}
            width={image.width}
            alt={image.alt}
            className='w-100 object-fit-contain'
          />
        </div>
      )}
    </div>
  )
}

export type PageType = {
  addons: TAddon[]
  plans: TPlanSummary[]
}

const Page: NextPageWithLayout<PageType> = ({ addons, plans }) => {
  const router = useRouter()
  const size = useWindowSize()
  const { addPlan, addPlanAddon, value } = useShoppingBag()
  const [shouldAddCrunchAddon, setShouldAddCrunchAddon] = useState(false)

  const companyFormationBreadcrumb: ItemListElements[] = [
    homeBreadcrumb()[0],
    {
      item: 'https://www.hoxtonmix.com/free-accounting-service/',
      name: `${Strings.companyFormation}`,
      position: 2,
    },
  ]

  const seoProps: SeoContainerProps['seoProps'] = {
    description:
      'Simple, powerful accounting software built for founders – not accountants. Send invoices, create quotes, and get paid faster. No jargon. No fees. Just free.',
    openGraph: {
      url: 'https://www.hoxtonmix.com/free-accounting-service/',
    },
    title: 'Free Accounting Software for Business Owners | Hoxton Mix',
  }
  const crunchAddon = addons.find((addon) => addon.slug === AddonSlug.CRUNCH)
  const VOPlan = plans.find((v) => v.slug === PlanSlug.SCAN_LITE)

  useEffect(() => {
    if (shouldAddCrunchAddon && value.slug) {
      if (crunchAddon) {
        addPlanAddon(crunchAddon.slug, crunchAddon)
        setShouldAddCrunchAddon(false)
        router.push('/checkout')
      }
    }
  }, [shouldAddCrunchAddon, value])

  const handleSignUp = () => {
    if (crunchAddon && VOPlan) {
      addPlan(VOPlan?.slug, 'year', VOPlan)
      setShouldAddCrunchAddon(true)
    }
  }

  return (
    <SeoContainer breadcrumb={companyFormationBreadcrumb} seoProps={seoProps}>
      <main className={'overflow-x-hidden new-design d-flex flex-column'}>
        <PartnerHeroBanner
          partnershipLogo={
            <div
              className={
                'd-flex flex-wrap justify-content-center justify-content-lg-start'
              }
            >
              <CrunchLogoPink className='max-w-100' />
            </div>
          }
          robotImage={{
            height: 760,
            src: '/images/new/crunch/crunch-robot-pointing-left.png',
            width: 926,
          }}
          robotImageMobile={{
            height: 515,
            src: '/images/new/crunch/crunch-robot-holding-mail.png',
            width: 393,
          }}
          imageContainerWidth={size.isLgOrHigher ? '66%' : '100%'}
          imageContainerClassName='pe-xxxl-28 pe-lg-16'
          title={
            <div className='me-xxl-n35 me-lg-n9 mt-lg-10'>
              Free accounting software that's built for you, not
              accountants
            </div>
          }
          description={
            <div className='mt-lg-6 lh-140 me-lg-n40'>
              Simple, powerful tools that do exactly what you need — nothing
              more, nothing less — so you can spend less time on accounting and
              more time growing your business.
            </div>
          }
          ctaBtn={
            <Button onClick={handleSignUp} className='w-100 w-lg-auto mt-lg-5'>
              Sign Up
            </Button>
          }
        />

        <div
          className={classNames(
            'container d-flex flex-column gap-4 mt-10 mt-lg-24',
            sectionGapSmall,
          )}
        >
          <img
            {...generateSrcSet(
              '/images/new/crunch/crunch-platform-ui.png',
              1350,
              815,
            )}
            src='/images/new/crunch/crunch-platform-ui.png'
            height={815}
            width={1350}
            alt='Crunch Platform UI'
            className='img-fluid'
            style={{
              filter: 'drop-shadow(0px 0px 12px rgba(0, 0, 0, 0.06))',
            }}
          />
          <div className='d-flex flex-column flex-lg-row gap-9'>
            <CrunchTestimonialCard
              name='Philippa'
              testimonial='"Easy to use and helpful to have everything I need in one place. Free tier is great until I grow my needs and it looks simple to upgrade."'
            />
            <CrunchTestimonialCard
              name='Mark'
              testimonial={`“I've used the free service to issue invoices and track payment while operating as a sole trader and it was very helpful. I added the VAT subscription to be able to submit VAT returns and that worked very well for a small cost.”`}
            />
          </div>
        </div>
        <div
          className={classNames(
            'container d-flex flex-column gap-4 mt-10 mt-lg-24',
            sectionGap,
          )}
        >
          <FeatureSection
            title='Invoicing'
            features={[
              {
                description:
                  'Send invoices in seconds with our easy to use, mobile-friendly app. Create, customise, and send with just one click.',
                title: 'Free invoicing software',
              },
              {
                description:
                  'Create quotes on the go to boost your chance of winning new business. Less time, less hassle, more sales.',
                title: 'Quick quotes that seal the deal',
              },
              {
                description: `Just add a 'Pay Now' button to your invoices, and clients can pay with whatever works for them—Mobile Payments, Instant Bank Transfers, and more.`,
                title: 'Get paid 3x faster',
              },
            ]}
            imagePosition='right'
            image={{
              alt: 'Crunch Invoicing UI',
              height: 499,
              src: '/images/new/crunch/crunch-invoicing-ui.png',
              width: 660,
            }}
          />
          <FeatureSection
            title='Expenses'
            features={[
              {
                description: (
                  <>
                    Easily track all your expenses with no limits. Our free
                    accounting software automatically applies the correct VAT
                    rate, saving you time and reducing your tax hassle.
                    <br />
                    <br />
                    Say goodbye to manually tracking subscriptions and recurring
                    expenses. Add them once, and we'll handle the rest!
                  </>
                ),
                title: 'Unlimited free expenses',
              },
              {
                description: (
                  <>
                    Imagine snapping a photo of your receipt and instantly
                    having it added to your records. No manual entry, no hassle.
                    <br />
                    <br />
                    Our Snap app makes it that easy, helping you save time while
                    claiming expenses to lower your tax bill.
                  </>
                ),
                title: 'Snap and store receipts in seconds',
              },
            ]}
            imagePosition='left'
            image={{
              alt: 'Crunch Expenses UI',
              height: 499,
              src: '/images/new/crunch/crunch-expense-ui.png',
              width: 660,
            }}
          />
          <FeatureSection
            title='Banking'
            features={[
              {
                description: (
                  <>
                    Ready to stop manually importing bank statements?
                    Our free Open Banking integration securely syncs your business
                    transactions automatically.
                    <br />
                    <br />
                    We connect to most major UK banks, so your data updates
                    daily without any extra steps. It's one less thing to
                    manage — thanks to our free accounting software.
                  </>
                ),
                title: 'Free Open Banking',
              },
              {
                description: (
                  <>
                    No more chasing transactions or second-guessing your
                    accounts. With our free accounting software, bank
                    reconciliation is effortless. Open Banking integration
                    and one-click reconciling means you're always tax-ready.
                    <br />
                    <br />
                    Need help? Our comprehensive Help Centre is packed with
                    clear guides and resources. Try it free and
                    leave reconciliation headaches behind.
                  </>
                ),
                title: 'Bank reconciliation made simple',
              },
              {
                description: (
                  <>
                    No Open Banking integration for your bank? No problem. With
                    Crunch, you can easily upload your bank statement via CSV.
                    <br />
                    <br />
                    Just upload from any major bank and your accounts stay
                    accurate and up to date — no hassle.
                  </>
                ),
                title: 'CSV Statement upload',
              },
            ]}
            imagePosition='right'
            image={{
              alt: 'Crunch Banking UI',
              height: 499,
              src: '/images/new/crunch/crunch-banking-ui.png',
              width: 660,
            }}
          />
          <FeatureSection
            title='Tax/VAT'
            features={[
              {
                description: (
                  <>
                    Filing your Self Assessment tax return doesn't have to be a
                    headache. For a one-off fee, our Chartered Certified
                    Accountants can handle everything—preparing your return,
                    making sure you're paying the right amount of tax, and
                    filing it with HMRC once you approve it.
                    <br />
                    <br />
                    As your business grows, you can add extra support whenever
                    you need it while still enjoying our free accounting
                    software. For even more benefits, our accounting packages
                    include unlimited tax advice and hands-on support from our
                    accountants.
                  </>
                ),
                title: 'Self Assessment filing',
              },
              {
                description: (
                  <>
                    Crunch Free software automatically includes VAT in all
                    relevant invoices, whether you're on Flat Rate or Standard
                    Rate. VAT-registered businesses can easily file their VAT
                    returns each quarter through our free accounting software,
                    for just £4.00 plus VAT per month. Keeping you fully Making
                    Tax Digital compliant.
                  </>
                ),
                title: 'Making Tax Digital VAT return filing',
              },
            ]}
            imagePosition='left'
            image={{
              alt: 'Crunch Expenses UI',
              height: 499,
              src: '/images/new/crunch/crunch-expense-ui.png',
              width: 660,
            }}
          />
          <FeatureSection
            title='Support'
            features={[
              {
                description: (
                  <>
                    Got tax questions? Need expert advice on paying yourself
                    efficiently? Book a one-off call with a Chartered Certified
                    Accountant and get answers tailored to your business.
                    <br />
                    <br />
                    For just £24.50+VAT, you'll get clear, practical guidance
                    plus a detailed report of everything discussed. No ongoing
                    commitment needed! If you upgrade to a Limited package
                    within 30 days, we'll even refund the fee.
                  </>
                ),
                title: 'Ask an accountant',
              },
              {
                description: (
                  <>
                    Need a hand using Crunch Free? Our Help Centre is packed
                    with guides, tips and step-by-step instructions to help you
                    get the most out of our free accounting software. No jargon,
                    just clear, practical help whenever you need it.
                  </>
                ),
                title: 'Free Help Centre',
              },
              {
                description: (
                  <>
                    Running a business comes with questions, and our Crunch
                    Chorus community is here to help. Connect with other
                    entrepreneurs, get advice, and share your own experiences.
                  </>
                ),
                title: 'Crunch Chorus community',
              },
            ]}
            imagePosition='right'
            image={{
              alt: 'Crunch Support UI',
              height: 499,
              src: '/images/new/crunch/crunch-support-ui.png',
              width: 660,
            }}
          />
          <div className='d-flex flex-column flex-lg-row gap-8'>
            <CrunchTestimonialCard
              name='Michael'
              testimonial='"Great app with lots of features. The free tier is essential for startup freelancing"'
            />
            <CrunchTestimonialCard
              name='Louise'
              testimonial='"A good free option for businesses needing basic bookkeeping."'
            />
          </div>
          <FeatureSection
            features={[]}
            title='Who we help'
            imagePosition='left'
            image={{
              alt: 'Crunch Who We Help',
              height: 499,
              src: '/images/new/crunch/crunch-who-we-help.png',
              width: 660,
            }}
          >
            <div className='d-flex flex-column'>
              {whoWeHelp.map((item) => (
                <span key={item} className='fs-24 fw-semibold'>
                  {item}
                </span>
              ))}
            </div>
          </FeatureSection>
          <RightRobotCard
            image={{
              alt: 'Robot arm pointing left for privacy service',
              desktop: {
                dimensions: {
                  height: 246,
                  width: 697,
                },
                src: '/images/new/arm-pointing-left.png',
              },
              mobile: {
                dimensions: {
                  height: 265,
                  width: 259,
                },
                src: '/images/new/arm-pointing-down-angle.png',
              },
            }}
            imageContainerWidth={'50%'}
            reverseColumnOrder
            overrideAllImageStyles={true}
            imageStyle={{
              maxHeight: '100%',
              objectFit: 'contain',
              top: 50,
              width: '100%',
            }}
          >
            <div className='d-flex flex-column flex-1 px-4 pb-8 py-lg-9 ps-lg-20'>
              <h2 className='fs-fluid-36-80 lh-105 fw-semibold mb-4 mb-lg-5 me-lg-10'>
                Take control of your{' '}
                <span className='color-pink4'>accounts</span>, today
              </h2>
              <div className='fs-fluid-20-24 lh-140 mt-lg-8 fw-semibold me-lg-n16'>
                Crunch's free accounting software for small businesses gives you
                the tools you need to manage your finances with ease. Need more
                support? Upgrade anytime for access to tax experts and Chartered
                Certified Accountants.
              </div>
            </div>
          </RightRobotCard>
        </div>
      </main>
    </SeoContainer>
  )
}

export default Page

export async function getStaticProps(
  context: I18NConfig & { locale?: string; params: { uid: string } },
) {
  try {
    const addons = await getAddons(true)
    const plans = await getPlans()

    return {
      props: {
        addons,
        plans,
      },
    }
  } catch (e) {
    return {
      props: {},
    }
  }
}
